/**
 * Images — viewing and editing (§8 Phase 1, §19).
 *
 * Edits are stored as an **ordered list of operations**, not as a mutated
 * bitmap. Undo and redo (§12) then cost nothing but an index, and the original
 * decoded pixels are never overwritten — which is what makes §13's "the
 * original is not changed" true at the pixel level and not merely at the file
 * level.
 *
 * Two honest boundaries:
 *
 * - **PNG decodes and encodes here.** Everything else needs the platform's
 *   decoder, injected as {@link ImageDecoderPort}. Without one, a JPEG reports
 *   that it cannot be opened rather than showing something wrong.
 * - **Text is rasterised by the platform.** Drawing a glyph requires a font and
 *   a shaper; shipping a half-working one would break exactly the scripts §25
 *   cares about. The platform renders the text to RGBA and it is composited
 *   here as a stamp.
 */
import {
  adjust, blur, compositeOver, crop, drawLine, drawPolyline, fillRect,
  fitWithin, flipHorizontal, flipVertical, grayscale, mosaic, probeImage,
  resize, rotate, strokeRect,
} from '@moduviewer/core';
import type { Adjustments, Rect, Rgba, RgbaImage, Rotation } from '@moduviewer/core';
import type {
  ExportResult, FileSource, FormatHandler, HandlerContext,
  HandlerWarning, OpenedDocument, Preview, ValidationReport,
} from '../contract.js';
import { HandlerError, throwIfAborted } from '../contract.js';
import { decodePng, encodePng, isPng } from './png.js';

/** Supplies decoded pixels for formats this package cannot decode itself. */
export interface ImageDecoderPort {
  /** Return null when the platform cannot decode this format either. */
  decode(bytes: Uint8Array, formatId: string): Promise<RgbaImage | null>;
}

export type ImageEdit =
  | { readonly kind: 'crop'; readonly rect: Rect }
  | { readonly kind: 'rotate'; readonly degrees: Rotation }
  | { readonly kind: 'flip'; readonly axis: 'horizontal' | 'vertical' }
  | { readonly kind: 'resize'; readonly width: number; readonly height: number }
  | { readonly kind: 'mosaic'; readonly rect: Rect; readonly blockSize: number }
  | { readonly kind: 'blur'; readonly rect: Rect; readonly radius: number }
  | { readonly kind: 'adjust'; readonly options: Adjustments }
  | { readonly kind: 'grayscale' }
  | { readonly kind: 'fill'; readonly rect: Rect; readonly color: Rgba }
  | { readonly kind: 'stroke'; readonly rect: Rect; readonly color: Rgba; readonly thickness: number }
  | {
    readonly kind: 'line';
    readonly from: readonly [number, number];
    readonly to: readonly [number, number];
    readonly color: Rgba;
    readonly thickness: number;
  }
  | {
    readonly kind: 'draw';
    readonly points: readonly (readonly [number, number])[];
    readonly color: Rgba;
    readonly thickness: number;
  }
  /** Pre-rendered overlay — text, a signature, a stamp. */
  | {
    readonly kind: 'stamp';
    readonly overlay: RgbaImage;
    readonly x: number;
    readonly y: number;
    readonly opacity: number;
  };

export interface ImageModel {
  /** Decoded original. Never modified. */
  readonly source: RgbaImage;
  /** Applied edits, oldest first. */
  readonly edits: readonly ImageEdit[];
  /** How many of `edits` are currently applied — the undo cursor (§12). */
  readonly cursor: number;
  readonly formatId: string;
  /** Only PNG can be written by this package. */
  readonly exportFormatId: 'png';
  readonly originalWidth: number;
  readonly originalHeight: number;
}

/** Apply the edits up to the cursor. Pure, so it is safe to recompute freely. */
export function renderImage(model: ImageModel): RgbaImage {
  let image = model.source;
  for (const edit of model.edits.slice(0, model.cursor)) {
    switch (edit.kind) {
      case 'crop': image = crop(image, edit.rect); break;
      case 'rotate': image = rotate(image, edit.degrees); break;
      case 'flip':
        image = edit.axis === 'horizontal' ? flipHorizontal(image) : flipVertical(image);
        break;
      case 'resize': image = resize(image, edit.width, edit.height); break;
      case 'mosaic': image = mosaic(image, edit.rect, edit.blockSize); break;
      case 'blur': image = blur(image, edit.rect, edit.radius); break;
      case 'adjust': image = adjust(image, edit.options); break;
      case 'grayscale': image = grayscale(image); break;
      case 'fill': image = fillRect(image, edit.rect, edit.color); break;
      case 'stroke': image = strokeRect(image, edit.rect, edit.color, edit.thickness); break;
      case 'line': image = drawLine(image, edit.from, edit.to, edit.color, edit.thickness); break;
      case 'draw': image = drawPolyline(image, edit.points, edit.color, edit.thickness); break;
      case 'stamp':
        image = compositeOver(image, edit.overlay, edit.x, edit.y, edit.opacity);
        break;
      default: {
        const never: never = edit;
        void never;
      }
    }
  }
  return image;
}

/* ------------------------------------------------------------------ *
 * edit helpers (§12 undo / redo)
 * ------------------------------------------------------------------ */

export function applyEdit(model: ImageModel, edit: ImageEdit): ImageModel {
  // Editing after an undo discards the redo tail, which is what every editor
  // does and what users expect.
  const edits = [...model.edits.slice(0, model.cursor), edit];
  return { ...model, edits, cursor: edits.length };
}

export function undo(model: ImageModel): ImageModel {
  return model.cursor > 0 ? { ...model, cursor: model.cursor - 1 } : model;
}

export function redo(model: ImageModel): ImageModel {
  return model.cursor < model.edits.length ? { ...model, cursor: model.cursor + 1 } : model;
}

export function canUndo(model: ImageModel): boolean { return model.cursor > 0; }
export function canRedo(model: ImageModel): boolean { return model.cursor < model.edits.length; }
export function isEdited(model: ImageModel): boolean { return model.cursor > 0; }

/* ------------------------------------------------------------------ *
 * handler
 * ------------------------------------------------------------------ */

export interface ImageHandlerOptions {
  /** Platform decoder for formats other than PNG. */
  readonly decoder?: ImageDecoderPort;
}

const RASTER_FORMAT_IDS = ['png', 'jpeg', 'gif', 'webp', 'bmp', 'ico', 'avif', 'tiff'] as const;

export function createImageHandler(options: ImageHandlerOptions = {}): FormatHandler<ImageModel> {
  const decoder = options.decoder;

  async function decode(source: FileSource, ctx: HandlerContext): Promise<RgbaImage> {
    // Dimensions come from the header first: decoding a 40000×40000 image to
    // find out it is too big is exactly the failure §33 exists to prevent.
    const probe = probeImage(source.bytes);
    if (probe.ok) {
      const pixels = probe.dimensions.width * probe.dimensions.height;
      if (pixels > ctx.limits.maxDecodedPixels) {
        throw new HandlerError(
          'error.unableToRead',
          `${probe.dimensions.width}×${probe.dimensions.height} exceeds the decode budget of ${ctx.limits.maxDecodedPixels} pixels`,
        );
      }
    }

    if (isPng(source.bytes)) return decodePng(source.bytes).image;

    if (decoder) {
      const decoded = await decoder.decode(source.bytes, source.formatId);
      if (decoded) return decoded;
    }
    throw new HandlerError(
      'error.featuresUnsupported',
      `no decoder available for ${source.formatId} on this platform`,
    );
  }

  return {
    id: 'image',
    formatIds: RASTER_FORMAT_IDS,

    async probe(source: FileSource, ctx: HandlerContext): Promise<Preview> {
      throwIfAborted(ctx.signal);
      const result = probeImage(source.bytes);
      if (!result.ok) {
        throw new HandlerError('error.damagedOrIncomplete', `image header unreadable: ${result.reason}`);
      }
      const { width, height } = result.dimensions;
      // §11: a thumbnail-sized preview appears before the full decode.
      const thumb = fitWithin(width, height, 320, 320);
      return {
        kind: 'image',
        unitCount: result.dimensions.frameCount,
        unitKey: undefined,
        firstContent: {
          width, height,
          previewWidth: thumb.width, previewHeight: thumb.height,
          hasAlpha: result.dimensions.hasAlpha,
        },
        warnings: [],
      };
    },

    async open(source: FileSource, ctx: HandlerContext): Promise<OpenedDocument<ImageModel>> {
      throwIfAborted(ctx.signal);
      const image = await decode(source, ctx);
      const warnings: HandlerWarning[] = [];

      if (source.formatId !== 'png') {
        warnings.push({
          messageKey: 'error.formattingMayNotBePreserved',
          severity: 'caution',
          detail: `편집본은 PNG로 저장됩니다 (원본 형식: ${source.formatId})`,
        });
      }

      return {
        formatId: source.formatId,
        model: {
          source: image,
          edits: [],
          cursor: 0,
          formatId: source.formatId,
          exportFormatId: 'png',
          originalWidth: image.width,
          originalHeight: image.height,
        },
        readOnly: ctx.assessment.forceReadOnly,
        editLevel: source.formatId === 'png' ? 'full-edit' : 'convert-edit',
        lossOnSave: source.formatId === 'png' ? [] : [
          `편집본은 PNG로 저장됩니다. 원본 ${source.formatId} 형식으로는 다시 쓰지 않습니다.`,
        ],
        warnings,
      };
    },

    async exportBytes(
      model: ImageModel, source: FileSource, ctx: HandlerContext,
    ): Promise<ExportResult> {
      throwIfAborted(ctx.signal);
      const rendered = renderImage(model);
      const bytes = encodePng(rendered);

      const base = source.filename.replace(/\.[^.]+$/, '');
      const dropped: string[] = [];
      if (model.formatId !== 'png') dropped.push(`원본 ${model.formatId} 인코딩`);
      // Metadata is not carried across: EXIF holds GPS coordinates and device
      // identifiers, and silently copying them into a file the user is about to
      // share would leak location data (§29).
      dropped.push('EXIF 등 원본 메타데이터 (위치정보 유출 방지)');

      return {
        bytes,
        suggestedName: `${base}.png`,
        formatId: 'png',
        dropped,
      };
    },

    async validateExport(
      bytes: Uint8Array, model: ImageModel, ctx: HandlerContext,
    ): Promise<ValidationReport> {
      throwIfAborted(ctx.signal);
      const issues: string[] = [];
      let reopened = false;
      let contentPreserved = false;

      try {
        const { image } = decodePng(bytes);
        reopened = true;
        const expected = renderImage(model);

        if (image.width !== expected.width || image.height !== expected.height) {
          issues.push(
            `size changed: ${expected.width}×${expected.height} → ${image.width}×${image.height}`,
          );
        } else {
          // PNG is lossless, so a byte-exact comparison is the right assertion —
          // anything less would hide an encoder bug.
          let differing = 0;
          for (let i = 0; i < expected.data.length; i++) {
            if (image.data[i] !== expected.data[i]) { differing++; if (differing > 8) break; }
          }
          if (differing > 0) issues.push(`${differing}+ channel values differ after reopening`);
        }
        contentPreserved = issues.length === 0;
      } catch (err) {
        issues.push(`could not reopen the exported image: ${(err as Error).message}`);
      }

      return { ok: reopened && contentPreserved, reopened, contentPreserved, issues };
    },
  };
}

/** PNG-only handler, for platforms that provide no decoder of their own. */
export const imageHandler = createImageHandler();
